import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { resellerPortalService } from '../services/AdminServices'
import { Copy, Check, ShoppingBag, Wallet, Ticket, TrendingUp, DollarSign, Clock, Info, ExternalLink, UserCircle, Save, Megaphone, Download, Image, FileText, Package } from 'lucide-react'
import './ResellerPortal.css'

const STORE_URL = 'https://growpro.club'
const COMMISSION_RATE = 20
const DISCOUNT_RATE = 5

const PATH_TO_TAB = {
  '/my-sales': 'sales',
  '/my-payouts': 'payouts',
  '/my-link': 'coupon',
  '/my-profile': 'profile',
  '/my-tools': 'tools'
}

const PRODUCTS = [
  {
    model: 'GP-BOX',
    name: 'GrowPro Box',
    tagline: 'Control total de tu indoor',
    description: '4 relés programables + sensor de temperatura y humedad. Automatización completa de luces, riego, extracción y ventilación.',
    features: ['4 relés independientes', 'Sensor AHT10 (T°/H%)', 'WiFi + MQTT/TLS', 'Control desde la app', 'Programación horaria', 'OTA updates'],
    color: '#3b82f6',
  },
  {
    model: 'GP-MINI',
    name: 'GrowPro Mini',
    tagline: 'Monitoreo inteligente',
    description: 'Sensor de temperatura y humedad compacto. Ideal para quienes quieren monitorear su cultivo sin automatización.',
    features: ['Sensor AHT10 (T°/H%)', 'WiFi + MQTT/TLS', 'Dashboard en tiempo real', 'Alertas configurables', 'VPD automático', 'Ultra bajo consumo'],
    color: '#10b981',
  },
  {
    model: 'GP-CAM',
    name: 'GrowPro Cam',
    tagline: 'Ojos en tu cultivo 24/7',
    description: 'Cámara ESP32 con timelapse automático. Captura fotos periódicas de tu cultivo para seguimiento visual.',
    features: ['Cámara OV2640', 'Timelapse automático', 'Fotos bajo demanda', 'Almacenamiento S3', 'Visión nocturna (IR)', 'Stream en la app'],
    color: '#f59e0b',
  },
  {
    model: 'GP-HIDRO',
    name: 'GrowPro Hidro',
    tagline: 'Cultivo hidropónico automatizado',
    description: 'Sistema de control para cultivo hidropónico. Monitoreo y automatización de pH, EC, temperatura de agua y nutrientes.',
    features: ['Control de pH y EC', 'Sensor de temperatura de agua', 'Dosificación automática', 'WiFi + MQTT/TLS', 'Dashboard en tiempo real', 'Alertas configurables'],
    color: '#8b5cf6',
  },
]

const SELLING_TIPS = [
  { title: 'Enfocate en el problema', text: 'El cultivador indoor pierde cosechas por no controlar temperatura y humedad. GrowPro resuelve eso automáticamente.' },
  { title: 'Demostrá la app', text: 'Mostrá el dashboard en tu teléfono — el efecto "wow" de ver datos en tiempo real cierra la venta.' },
  { title: 'Ofrecé el combo', text: 'GP-BOX + GP-CAM es el combo más vendido. Automatización + monitoreo visual = cultivo profesional.' },
  { title: 'Mencioná el ahorro', text: 'Sin GrowPro, necesitan timer + termómetro + hidrómetro + estar ahí. Con GP-BOX, todo desde el celular.' },
  { title: 'Compartí tu cupón', text: 'Tu cliente obtiene 5% de descuento y vos ganás comisión. Win-win.' },
]

export default function ResellerPortal() {
  const location = useLocation()
  const initialTab = PATH_TO_TAB[location.pathname] || 'dashboard'
  const [profile, setProfile] = useState(null)
  const [sales, setSales] = useState([])
  const [payouts, setPayouts] = useState([])
  const [tab, setTab] = useState(initialTab)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(null)
  const [profileForm, setProfileForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState(null)

  const loadProfile = useCallback(async () => {
    try {
      const res = await resellerPortalService.getMe()
      setProfile(res.reseller)
    } catch (e) {
      setError(e.message)
    }
  }, [])

  const loadSales = useCallback(async () => {
    try {
      const res = await resellerPortalService.mySales()
      setSales(res.sales || [])
    } catch { /* noop */ }
  }, [])

  const loadPayouts = useCallback(async () => {
    try {
      const res = await resellerPortalService.myPayouts()
      setPayouts(res.payouts || [])
    } catch { /* noop */ }
  }, [])

  useEffect(() => {
    Promise.all([loadProfile(), loadSales(), loadPayouts()]).finally(() => setLoading(false))
  }, [loadProfile, loadSales, loadPayouts])

  useEffect(() => {
    setTab(PATH_TO_TAB[location.pathname] || 'dashboard')
  }, [location.pathname])

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
  }

  const initProfileForm = useCallback(() => {
    if (profile) {
      setProfileForm({
        name: profile.name || '',
        phone: profile.phone || '',
        businessName: profile.businessName || '',
        cbu: profile.cbu || '',
        alias: profile.alias || '',
        mpPayerEmail: profile.mpPayerEmail || '',
      })
    }
  }, [profile])

  useEffect(() => { initProfileForm() }, [initProfileForm])

  const saveProfile = async () => {
    if (!profileForm.name.trim()) { setSaveMsg({ type: 'error', text: 'El nombre es requerido' }); return }
    setSaving(true)
    setSaveMsg(null)
    try {
      await resellerPortalService.updateMe(profileForm)
      await loadProfile()
      setSaveMsg({ type: 'ok', text: 'Datos guardados correctamente' })
      setTimeout(() => setSaveMsg(null), 3000)
    } catch (e) {
      setSaveMsg({ type: 'error', text: e.message })
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="rp-loading">Cargando portal...</div>
  if (error) return <div className="rp-error">{error}</div>
  if (!profile) return <div className="rp-error">No se encontró tu perfil de revendedor</div>

  const couponCode = (profile.code || '').toLowerCase()
  const referralLink = `https://app.growpro.club/?ref=${couponCode}`
  const verifiedSales = sales.filter(s => ['verified', 'approved'].includes(s.commissionStatus))
  const paidSales = sales.filter(s => s.commissionStatus === 'paid')
  const pendingSales = sales.filter(s => !['paid', 'rejected'].includes(s.commissionStatus))

  return (
    <div className="rp-container">
      <div className="rp-header">
        <div>
          <h1>Hola, {profile.name}</h1>
          <p className="rp-subtitle">Portal de Revendedor GrowPro</p>
        </div>
        <div className="rp-status-badge" data-status={profile.status}>
          {profile.status === 'active' ? 'Activo' : profile.status === 'pending' ? 'Pendiente' : 'Suspendido'}
        </div>
      </div>

      {/* Tabs */}
      <div className="rp-tabs">
        <button className={tab === 'dashboard' ? 'active' : ''} onClick={() => setTab('dashboard')}>
          <TrendingUp size={16} /> Dashboard
        </button>
        <button className={tab === 'sales' ? 'active' : ''} onClick={() => setTab('sales')}>
          <ShoppingBag size={16} /> Mis Ventas
        </button>
        <button className={tab === 'payouts' ? 'active' : ''} onClick={() => setTab('payouts')}>
          <Wallet size={16} /> Mis Pagos
        </button>
        <button className={tab === 'coupon' ? 'active' : ''} onClick={() => setTab('coupon')}>
          <Ticket size={16} /> Mi Cupón
        </button>
        <button className={tab === 'profile' ? 'active' : ''} onClick={() => setTab('profile')}>
          <UserCircle size={16} /> Mi Perfil
        </button>
        <button className={tab === 'tools' ? 'active' : ''} onClick={() => setTab('tools')}>
          <Megaphone size={16} /> Herramientas
        </button>
      </div>

      {/* Dashboard */}
      {tab === 'dashboard' && (
        <div className="rp-dashboard">
          <div className="rp-stats">
            <div className="rp-stat-card">
              <ShoppingBag size={24} />
              <div>
                <span className="rp-stat-value">{profile.totalSales || 0}</span>
                <span className="rp-stat-label">Ventas Totales</span>
              </div>
            </div>
            <div className="rp-stat-card">
              <DollarSign size={24} />
              <div>
                <span className="rp-stat-value">${(profile.totalCommission || 0).toLocaleString()}</span>
                <span className="rp-stat-label">Comisión Total</span>
              </div>
            </div>
            <div className="rp-stat-card highlight">
              <Clock size={24} />
              <div>
                <span className="rp-stat-value">${(profile.pendingCommission || 0).toLocaleString()}</span>
                <span className="rp-stat-label">Comisión Pendiente</span>
              </div>
            </div>
            <div className="rp-stat-card">
              <Wallet size={24} />
              <div>
                <span className="rp-stat-value">${(profile.paidCommission || 0).toLocaleString()}</span>
                <span className="rp-stat-label">Comisión Cobrada</span>
              </div>
            </div>
          </div>

          {/* Coupon card */}
          <div className="rp-coupon-card">
            <div className="rp-coupon-card-left">
              <h3><Ticket size={18} /> Tu Cupón de Descuento</h3>
              <div className="rp-coupon-display">
                <code className="rp-coupon-code">{couponCode}</code>
                <button onClick={() => copyText(couponCode, 'code')} className="rp-copy-btn">
                  {copied === 'code' ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
              <p className="rp-coupon-hint">
                Tu cliente obtiene <strong>{DISCOUNT_RATE}% de descuento</strong> en la tienda y vos ganás <strong>{COMMISSION_RATE}% de comisión</strong> por cada venta.
              </p>
            </div>
            <div className="rp-coupon-card-right">
              <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="rp-store-link">
                <ExternalLink size={14} /> Ir a la tienda
              </a>
            </div>
          </div>

          {/* How it works */}
          <div className="rp-how-it-works">
            <Info size={16} />
            <span>Compartí tu código <strong>{couponCode}</strong>. Cuando un cliente lo usa en <strong>growpro.club</strong>, la venta se registra automáticamente y tu comisión se calcula al instante.</span>
          </div>

          {/* Recent sales */}
          {sales.length > 0 && (
            <div className="rp-recent">
              <h3>Últimas Ventas</h3>
              {sales.slice(0, 5).map(sale => (
                <div key={sale.$id || sale.SK} className="rp-sale-row">
                  <div>
                    <span className="rp-sale-customer">{sale.customerName || 'Cliente'}</span>
                    <span className="rp-sale-product">
                      {sale.products ? sale.products.map(p => `${p.model}×${p.qty}`).join(', ') : sale.product}
                    </span>
                  </div>
                  <div className="rp-sale-right">
                    <span className="rp-sale-amount">${(sale.commissionAmount || 0).toLocaleString()}</span>
                    <span className={`rp-sale-status ${sale.commissionStatus}`}>{
                      sale.commissionStatus === 'paid' ? 'Pagada' :
                      sale.commissionStatus === 'verified' ? 'Verificada' :
                      sale.commissionStatus === 'approved' ? 'Aprobada' :
                      sale.commissionStatus === 'rejected' ? 'Rechazada' :
                      'Pendiente'
                    }</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sales tab */}
      {tab === 'sales' && (
        <div className="rp-sales">
          <div className="rp-sales-header">
            <h2>Mis Ventas ({sales.length})</h2>
          </div>
          <div className="rp-how-it-works" style={{ marginBottom: 16 }}>
            <Info size={16} />
            <span>Las ventas se registran automáticamente cuando un cliente usa tu cupón <strong>{couponCode}</strong> en la tienda.</span>
          </div>
          {sales.length === 0 ? (
            <div className="rp-empty">Todavía no tenés ventas. Compartí tu cupón <strong>{couponCode}</strong> para empezar.</div>
          ) : (
            <div className="rp-table-wrapper">
              <table className="rp-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Productos</th>
                    <th>Monto</th>
                    <th>Comisión</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map(sale => (
                    <tr key={sale.$id || sale.SK}>
                      <td>{sale.saleDate ? new Date(sale.saleDate).toLocaleDateString('es-AR') : '-'}</td>
                      <td>{sale.customerName || '-'}</td>
                      <td>{sale.products ? sale.products.map(p => `${p.model}×${p.qty}`).join(', ') : sale.product}</td>
                      <td>${(sale.saleAmount || 0).toLocaleString('es-AR')}</td>
                      <td>${(sale.commissionAmount || 0).toLocaleString('es-AR')}</td>
                      <td><span className={`rp-status-badge-sm ${sale.commissionStatus}`}>{
                        sale.commissionStatus === 'paid' ? 'Pagada' :
                        sale.commissionStatus === 'verified' ? 'Verificada' :
                        sale.commissionStatus === 'approved' ? 'Aprobada' :
                        sale.commissionStatus === 'rejected' ? 'Rechazada' :
                        'Pendiente'
                      }</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Payouts tab */}
      {tab === 'payouts' && (
        <div className="rp-payouts">
          <h2>Mis Pagos ({payouts.length})</h2>
          {payouts.length === 0 ? (
            <div className="rp-empty">No tenés pagos registrados aún</div>
          ) : (
            <div className="rp-table-wrapper">
              <table className="rp-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Monto</th>
                    <th>Método</th>
                    <th>Estado</th>
                    <th>Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map(p => (
                    <tr key={p.$id || p.SK}>
                      <td>{p.createdAt ? new Date(p.createdAt).toLocaleDateString('es-AR') : '-'}</td>
                      <td>${(p.amount || p.totalCommission || 0).toLocaleString()}</td>
                      <td>{p.method === 'transfer' ? 'Transferencia' : p.method === 'mercadopago' ? 'MercadoPago' : p.method || 'Auto'}</td>
                      <td><span className={`rp-status-badge-sm ${p.status}`}>{
                        p.status === 'completed' ? 'Completado' :
                        p.status === 'pending' ? 'Pendiente' : p.status
                      }</span></td>
                      <td>{p.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Coupon tab */}
      {tab === 'coupon' && (
        <div className="rp-coupon-tab">
          <div className="rp-coupon-main">
            <h2>Tu Cupón de Descuento</h2>
            <p>Cuando un cliente compra en <strong>growpro.club</strong> usando tu cupón, obtiene un <strong>{DISCOUNT_RATE}% de descuento</strong> y vos ganás <strong>{COMMISSION_RATE}% de comisión</strong> sobre el hardware.</p>

            <div className="rp-link-box">
              <label>Código de cupón</label>
              <div className="rp-link-row">
                <code className="rp-code-big">{couponCode}</code>
                <button onClick={() => copyText(couponCode, 'coupon')} className="rp-copy-btn-lg">
                  {copied === 'coupon' ? <><Check size={16} /> Copiado</> : <><Copy size={16} /> Copiar</>}
                </button>
              </div>
            </div>

            <div className="rp-link-box">
              <label>Link directo a la tienda</label>
              <div className="rp-link-row">
                <code>{STORE_URL}</code>
                <button onClick={() => copyText(STORE_URL, 'store')} className="rp-copy-btn-lg">
                  {copied === 'store' ? <><Check size={16} /> Copiado</> : <><Copy size={16} /> Copiar</>}
                </button>
              </div>
            </div>

            <div className="rp-link-box rp-link-box-highlight">
              <label>Link de registro con referido</label>
              <div className="rp-link-row">
                <code>{referralLink}</code>
                <button onClick={() => copyText(referralLink, 'referral')} className="rp-copy-btn-lg">
                  {copied === 'referral' ? <><Check size={16} /> Copiado</> : <><Copy size={16} /> Copiar</>}
                </button>
              </div>
              <p className="rp-link-hint">
                <Info size={14} /> Si un usuario se registra con este link y luego contrata una suscripción, la comisión se te asocia automáticamente.
              </p>
            </div>

            <div className="rp-coupon-how">
              <h3>¿Cómo funciona?</h3>
              <div className="rp-steps">
                <div className="rp-step">
                  <span className="rp-step-num">1</span>
                  <span>Compartí tu código <strong>{couponCode}</strong> con tus clientes</span>
                </div>
                <div className="rp-step">
                  <span className="rp-step-num">2</span>
                  <span>El cliente lo ingresa en el checkout de <strong>growpro.club</strong> y obtiene {DISCOUNT_RATE}% off</span>
                </div>
                <div className="rp-step">
                  <span className="rp-step-num">3</span>
                  <span>La venta se registra automáticamente en tu panel</span>
                </div>
                <div className="rp-step">
                  <span className="rp-step-num">4</span>
                  <span>Recibís tu comisión del {COMMISSION_RATE}% en la liquidación mensual</span>
                </div>
              </div>
            </div>

            <div className="rp-coupon-stats">
              <div><strong>{pendingSales.length}</strong> pendientes</div>
              <div><strong>{verifiedSales.length}</strong> verificadas</div>
              <div><strong>{paidSales.length}</strong> pagadas</div>
              <div><strong>{sales.length}</strong> total</div>
            </div>
          </div>
        </div>
      )}

      {/* Profile tab */}
      {tab === 'profile' && profileForm && (
        <div className="rp-profile">
          <h2>Mis Datos</h2>
          <p className="rp-profile-desc">Completá o actualizá tus datos. Esta información se usa para gestionar tus comisiones y pagos.</p>

          {saveMsg && (
            <div className={`rp-save-msg ${saveMsg.type}`}>
              {saveMsg.type === 'ok' ? <Check size={16} /> : <Info size={16} />}
              {saveMsg.text}
            </div>
          )}

          <div className="rp-profile-form">
            <div className="rp-profile-section">
              <h3>Información Personal</h3>
              <div className="rp-pf-row-2">
                <div className="rp-pf-field">
                  <label>Nombre *</label>
                  <input value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} placeholder="Tu nombre completo" />
                </div>
                <div className="rp-pf-field">
                  <label>Teléfono</label>
                  <input value={profileForm.phone} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))} placeholder="+54 11 1234 5678" />
                </div>
              </div>
              <div className="rp-pf-field">
                <label>Email</label>
                <input value={profile.email || ''} disabled className="rp-pf-disabled" />
                <span className="rp-pf-hint">El email no se puede cambiar</span>
              </div>
              <div className="rp-pf-field">
                <label>Negocio / Growshop</label>
                <input value={profileForm.businessName} onChange={e => setProfileForm(f => ({ ...f, businessName: e.target.value }))} placeholder="Nombre de tu negocio" />
              </div>
            </div>

            <div className="rp-profile-section">
              <h3>Datos de Pago</h3>
              <p className="rp-pf-section-hint">Necesitamos al menos un medio de pago para poder liquidar tus comisiones.</p>
              <div className="rp-pf-row-2">
                <div className="rp-pf-field">
                  <label>CBU</label>
                  <input value={profileForm.cbu} onChange={e => setProfileForm(f => ({ ...f, cbu: e.target.value }))} placeholder="CBU para transferencias" />
                </div>
                <div className="rp-pf-field">
                  <label>Alias bancario</label>
                  <input value={profileForm.alias} onChange={e => setProfileForm(f => ({ ...f, alias: e.target.value }))} placeholder="Ej: mi.alias.banco" />
                </div>
              </div>
              <div className="rp-pf-field">
                <label>Email de MercadoPago</label>
                <input type="email" value={profileForm.mpPayerEmail} onChange={e => setProfileForm(f => ({ ...f, mpPayerEmail: e.target.value }))} placeholder="Email de tu cuenta MP" />
              </div>
            </div>

            <div className="rp-profile-actions">
              <button className="rp-btn-primary" onClick={saveProfile} disabled={saving}>
                <Save size={16} /> {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'tools' && (
        <div className="rp-tools">
          {/* Product Catalog */}
          <div className="rp-tools-section">
            <h3><Package size={18} /> Catálogo de Productos</h3>
            <p className="rp-tools-subtitle">Conocé cada producto para poder venderlo mejor. Compartí esta info con tus clientes.</p>
            <div className="rp-product-grid">
              {PRODUCTS.map(p => (
                <div key={p.model} className="rp-product-card" style={{ borderTopColor: p.color }}>
                  <div className="rp-product-header" style={{ background: `${p.color}15` }}>
                    <span className="rp-product-model" style={{ color: p.color }}>{p.model}</span>
                    <h4>{p.name}</h4>
                    <p className="rp-product-tagline">{p.tagline}</p>
                  </div>
                  <p className="rp-product-desc">{p.description}</p>
                  <ul className="rp-product-features">
                    {p.features.map((f, i) => (
                      <li key={i}><Check size={14} style={{ color: p.color }} /> {f}</li>
                    ))}
                  </ul>
                  <a href={`${STORE_URL}/producto/${p.model.toLowerCase()}`} target="_blank" rel="noreferrer" className="rp-product-link">
                    Ver en tienda <ExternalLink size={14} />
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Marketing Copy */}
          <div className="rp-tools-section">
            <h3><FileText size={18} /> Textos para Vender</h3>
            <p className="rp-tools-subtitle">Copiá y pegá estos textos en tus redes o chats para ofrecer GrowPro.</p>
            <div className="rp-copy-templates">
              <CopyableText
                label="Mensaje general"
                text={`🌱 ¿Cultivás indoor? Te presento GrowPro — automatización y monitoreo desde el celular.\n\n✅ Controlá luces, riego, extracción y ventilación\n✅ Monitoreá temperatura y humedad en tiempo real\n✅ Recibí alertas si algo se descontrola\n\n👉 Comprá en ${STORE_URL}${profile?.code ? `\n🎁 Usá mi cupón ${profile.code.toUpperCase()} y obtené ${DISCOUNT_RATE}% OFF` : ''}`}
              />
              <CopyableText
                label="Para stories / reels"
                text={`🔥 Dejá de depender de timers y termómetros.\n\nCon GrowPro controlás todo tu indoor desde el celular 📱\n\n⚡ GP-BOX: 4 relés + sensor → automatización total\n📷 GP-CAM: fotos y timelapse de tu cultivo\n🌡️ GP-MINI: monitoreo T° y H% 24/7\n\n${profile?.code ? `Cupón ${profile.code.toUpperCase()} = ${DISCOUNT_RATE}% OFF → ` : ''}${STORE_URL}`}
              />
              <CopyableText
                label="Respuesta a consulta"
                text={`¡Buenas! Te cuento sobre GrowPro:\n\n🌱 Es un sistema IoT para cultivo indoor que se conecta por WiFi y lo manejás desde el celular.\n\n📦 GP-BOX ($$$): automatiza luces, riego, extracción y ventilación + sensor T°/H%\n🌡️ GP-MINI ($): solo monitoreo T°/H%, ideal si ya tenés timers\n📷 GP-CAM ($$): cámara con timelapse para seguir tu cultivo\n\nTodo se configura desde la app, sin cables extras.\n\n${profile?.code ? `🎁 Con mi cupón ${profile.code.toUpperCase()} tenés ${DISCOUNT_RATE}% de descuento.\n\n` : ''}Tienda: ${STORE_URL}`}
              />
            </div>
          </div>

          {/* Selling Tips */}
          <div className="rp-tools-section">
            <h3><TrendingUp size={18} /> Tips para Vender</h3>
            <div className="rp-tips-grid">
              {SELLING_TIPS.map((tip, i) => (
                <div key={i} className="rp-tip-card">
                  <span className="rp-tip-number">{i + 1}</span>
                  <div>
                    <h4>{tip.title}</h4>
                    <p>{tip.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="rp-tools-section">
            <h3><ExternalLink size={18} /> Links Útiles</h3>
            <div className="rp-links-grid">
              <a href={STORE_URL} target="_blank" rel="noreferrer" className="rp-link-card">
                <ShoppingBag size={20} />
                <div>
                  <h4>Tienda Online</h4>
                  <p>growpro.club</p>
                </div>
              </a>
              <a href="https://instagram.com/growpro.club" target="_blank" rel="noreferrer" className="rp-link-card">
                <Image size={20} />
                <div>
                  <h4>Instagram</h4>
                  <p>@growpro.club</p>
                </div>
              </a>
              {profile?.code && (
                <div className="rp-link-card rp-link-coupon" onClick={() => { navigator.clipboard.writeText(profile.code.toUpperCase()); setCopied(true); setTimeout(() => setCopied(false), 2000) }}>
                  <Ticket size={20} />
                  <div>
                    <h4>Tu Cupón</h4>
                    <p>{profile.code.toUpperCase()} — {copied ? '¡Copiado!' : 'Click para copiar'}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function CopyableText({ label, text }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <div className="rp-copy-block">
      <div className="rp-copy-block-header">
        <span>{label}</span>
        <button onClick={handleCopy} className="rp-copy-btn-small">
          {copied ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar</>}
        </button>
      </div>
      <pre className="rp-copy-block-text">{text}</pre>
    </div>
  )
}
